import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { PlanStatusProvider, usePlanStatus } from "./contexts/PlanStatusContext";

import Login from "./pages/auth/Login";
import Signup from "./pages/auth/Signup";
// import VerifyOtp from "./pages/auth/VerifyOtp";
import Home from "./pages/Home";

// auth guard
import Protected from "./components/common/Protected";

// dashboard layout + pages
import DashboardLayout from "./layouts/DashboardLayout";
import Details from "./pages/dashboard/profile/Details";
import Objective from "./pages/dashboard/profile/Objective";
import Funding from "./pages/dashboard/profile/Funding";
import Social from "./pages/dashboard/profile/Social";
import Discover from "./pages/dashboard/Discover";
import Write from "./pages/dashboard/Write";
import Track from "./pages/dashboard/Track";
import Organize from "./pages/dashboard/Organize";
import Report from "./pages/dashboard/Report";
import Support from "./pages/dashboard/Support";
import StartupToolkit from "./pages/dashboard/StartupToolkit";
import Community from "./pages/dashboard/Community";
import Team from "./pages/dashboard/profile/Team";
import AccountManagement from "./pages/dashboard/accountmanagement/AccountManagement";
import Notification from "./pages/dashboard/notification/Notification";
import Preferences from "./pages/dashboard/preferences/Preferences";
import Chat from "./pages/dashboard/chat/Chat";
import GlobalLoader from "./components/common/GlobalLoader";
import { Toaster } from "react-hot-toast";
import Dashboard from "./pages/Dashboard";
import Plans from "./pages/dashboard/Plans/Plans";
import CrowdFunding from "./pages/dashboard/CrowdFunding";
import InviteAcceptGate from "./pages/InviteAcceptGate";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import Audience from "./pages/dashboard/Audience";
import FundingToolkit from "./pages/dashboard/FundingToolkit";
import Onboarding from "./pages/onboarding/Onboarding";
import OnboardingGate from "./components/common/OnboardingGate";


interface DashboardShellProps {
  setProfileStatus: React.Dispatch<React.SetStateAction<boolean>>;
}

function DashboardShell({ setProfileStatus }: DashboardShellProps) {
  const handleProfileChange = (isOpen: boolean) => {
    // console.log("Profile status from Dashboard:", isOpen);
    setProfileStatus(isOpen); // update App state
  };

  return (
    <DashboardLayout onProfileChange={handleProfileChange}>
      <Outlet />
    </DashboardLayout>
  );
}

function SidebarRouteGuard({ children }: { children: React.ReactNode }) {
  const { loading, canAccessSidebarRoutes } = usePlanStatus();

  if (loading) return null;
  if (!canAccessSidebarRoutes) return <Navigate to="/dashboard/plans" replace />;
  return <>{children}</>;
}

export default function App() {
  const [profileStatus, setProfileStatus] = useState(false);

  // useEffect(() => {
    // console.log(profileStatus);
  // }, [profileStatus]);

  return (
    <AuthProvider>
      {/* <GlobalLoader /> */}
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          {/* <Route path="/verify" element={<VerifyOtp />} /> */}
          <Route path="/home" element={<Home />} />
          <Route
            path="/onboarding"
            element={
              <Protected>
                <Onboarding />
              </Protected>
            }
          />

          {/* Protected Dashboard - All dashboard routes under /dashboard */}
          <Route
            path="/dashboard"
            element={
              <Protected>
                <OnboardingGate>
                  <PlanStatusProvider>
                    <DashboardShell setProfileStatus={setProfileStatus} />
                  </PlanStatusProvider>
                </OnboardingGate>
              </Protected>
            }
          >
            {/* Default dashboard route - redirect to discover */}
            <Route
              index
              element={
                <SidebarRouteGuard>
                  <Dashboard />
                </SidebarRouteGuard>
              }
            />

            {/* Main sidebar routes */}
            <Route path="discover" element={<SidebarRouteGuard><Discover /></SidebarRouteGuard>} />
            <Route path="write/:savedGrantId" element={<SidebarRouteGuard><Write /></SidebarRouteGuard>} />
            <Route path="write" element={<SidebarRouteGuard><Write /></SidebarRouteGuard>} />
            <Route path="track" element={<SidebarRouteGuard><Track /></SidebarRouteGuard>} />
            <Route path="organize" element={<SidebarRouteGuard><Organize /></SidebarRouteGuard>} />
            <Route path="report" element={<SidebarRouteGuard><Report /></SidebarRouteGuard>} />
            <Route path="crowdfunding" element={<SidebarRouteGuard><CrowdFunding /></SidebarRouteGuard>} /> 
            <Route path="audience" element={<SidebarRouteGuard><Audience /></SidebarRouteGuard>} /> 
            <Route path="support" element={<SidebarRouteGuard><Support /></SidebarRouteGuard>} />
            <Route path="startuptoolkit" element={<SidebarRouteGuard><StartupToolkit /></SidebarRouteGuard>} />
            <Route path="fundingtoolkit" element={<SidebarRouteGuard><FundingToolkit /></SidebarRouteGuard>} />
            <Route path="community" element={<SidebarRouteGuard><Community /></SidebarRouteGuard>} />

            {/* Profile routes */}
            <Route path="profile/details" element={<Details />} />
            <Route path="profile/objective" element={<Objective />} />
            <Route path="profile/team" element={<Team />} />
            <Route path="profile/funding" element={<Funding />} />
            <Route path="profile/social" element={<Social />} />

            {/* Account Management */}
            <Route path="accountmanagement" element={<AccountManagement />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="terms-and-conditions" element={<TermsAndConditions />} />

            {/* Notification */}
            <Route path="notification" element={<Notification />} />
            {/* Preferences */}
            <Route path="preferences" element={<Preferences />} />
            {/* Chat */}
            <Route path="chat" element={<Chat />} />
            {/* Plans & Billing */}
            <Route path="plans" element={<Plans />} />
          </Route>

          {/* Invite-accept entry point from email link */}
          <Route path="/invite/accept/:token" element={<InviteAcceptGate />} />
          {/* Catch-all: back to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 2500,
          style: { fontSize: 14 },
          success: { iconTheme: { primary: "#10B981", secondary: "#fff" } },
          error: { iconTheme: { primary: "#EF4444", secondary: "#fff" } },
        }}
      />
    </AuthProvider>
  );
}
